/**
 * Nav Badges Context
 *
 * Provides nav badge state (red-dot notifications) for sidebar and home panels.
 * Polls GET /api/v1/external/nav-badges/ on an interval; acknowledges when user navigates to a section.
 *
 * @module contexts/NavBadgesContext
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getNavBadges, acknowledgeNavBadge } from '@/services/api/NavBadgesApiService';
import type { NavBadgeSection, NavBadgesResponse } from '@/types/navBadges';

const POLL_INTERVAL_MS = 15 * 1000; // 15 seconds

/** Min ms between GET requests to avoid burst duplicates. */
const FETCH_DEBOUNCE_MS = 2000;

/** Window key for the single app-wide polling interval (survives module duplicate loads e.g. on home with multiple chunks). */
const WINDOW_POLLING_KEY = '__loreNavBadgesPollingIntervalId';

function getGlobalIntervalId(): ReturnType<typeof setInterval> | null {
  if (typeof window === 'undefined') return null;
  const g = window as unknown as Record<string, unknown>;
  const id = g[WINDOW_POLLING_KEY];
  return typeof id === 'number' ? (id as unknown as ReturnType<typeof setInterval>) : null;
}

function setGlobalIntervalId(id: ReturnType<typeof setInterval> | null): void {
  if (typeof window === 'undefined') return;
  const g = window as unknown as Record<string, unknown>;
  g[WINDOW_POLLING_KEY] = id;
}

/** Last fetch timestamp (module-level) so all provider instances share debounce. */
let lastFetchAtModule = 0;

export type NavBadgesState = Record<NavBadgeSection, boolean>;

const defaultBadges: NavBadgesState = {
  rewards: false,
  community: false,
  lorebot: false,
  explorations: false,
  account: false,
  more: false,
};

function responseToState(data: NavBadgesResponse): NavBadgesState {
  return {
    rewards: data.rewards?.show ?? false,
    community: data.community?.show ?? false,
    lorebot: data.lorebot?.show ?? false,
    explorations: data.explorations?.show ?? false,
    account: data.account?.show ?? false,
    more: data.more?.show ?? false,
  };
}

interface NavBadgesContextType {
  badges: NavBadgesState;
  acknowledge: (section: NavBadgeSection) => Promise<void>;
}

const NavBadgesContext = createContext<NavBadgesContextType | null>(null);

function pathnameToSection(pathname: string): NavBadgeSection | null {
  const p = pathname.replace(/\/$/, '') || '/';
  if (p === '/rewards') return 'rewards';
  if (p === '/community') return 'community';
  if (p === '/lorebot') return 'lorebot';
  if (p === '/more') return 'more';
  if (p === '/account' || pathname.startsWith('/account/')) return 'account';
  return null;
}

interface NavBadgesProviderProps {
  children: ReactNode;
}

export function NavBadgesProvider({ children }: NavBadgesProviderProps) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === '1' || searchParams.get('embed') === 'true';
  const [badges, setBadges] = useState<NavBadgesState>(defaultBadges);
  const apiAvailableRef = useRef(false);
  const fetchBadgesRef = useRef<(() => Promise<void>) | null>(null);
  const acknowledgedSectionRef = useRef<NavBadgeSection | null>(null);

  const fetchBadges = useCallback(async () => {
    if (!isAuthenticated) return;
    const now = Date.now();
    if (now - lastFetchAtModule < FETCH_DEBOUNCE_MS) return;
    lastFetchAtModule = now;
    try {
      const data = await getNavBadges();
      if (data === null) {
        apiAvailableRef.current = false;
        return;
      }
      apiAvailableRef.current = true;
      const next = responseToState(data);
      setBadges(next);
      const active = (Object.entries(next) as [NavBadgeSection, boolean][]).filter(([, show]) => show).map(([s]) => s);
      console.log('[NavBadges] GET ok', active.length ? active : 'none');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        apiAvailableRef.current = false;
      }
    }
  }, [isAuthenticated]);

  fetchBadgesRef.current = fetchBadges;

  const acknowledge = useCallback(async (section: NavBadgeSection) => {
    setBadges((prev) => ({ ...prev, [section]: false }));
    await acknowledgeNavBadge(section);
  }, []);

  // Polling: only in top window. When we're in an iframe (embed=1, e.g. home page panels), skip polling so we don't get 3+ duplicate requests from each iframe.
  useEffect(() => {
    if (!isAuthenticated || isEmbed) {
      const existing = getGlobalIntervalId();
      if (existing !== null) {
        clearInterval(existing);
        setGlobalIntervalId(null);
      }
      if (!isAuthenticated) {
        setBadges(defaultBadges);
        apiAvailableRef.current = false;
      }
      if (isEmbed) console.log('[NavBadges] polling disabled (embed)');
      return;
    }
    const existing = getGlobalIntervalId();
    if (existing !== null) {
      clearInterval(existing);
      setGlobalIntervalId(null);
    }
    const tick = () => fetchBadgesRef.current?.();
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    setGlobalIntervalId(id);
    console.log('[NavBadges] polling started');
    return () => {
      const current = getGlobalIntervalId();
      if (current !== null) {
        clearInterval(current);
        setGlobalIntervalId(null);
      }
    };
  }, [isAuthenticated, isEmbed]);

  // Acknowledge only when user navigates to a section that currently has a badge (then clear it and notify server). Only once per section per visit.
  useEffect(() => {
    const section = pathnameToSection(location.pathname);
    if (!section) {
      acknowledgedSectionRef.current = null;
      return;
    }
    if (badges[section] && acknowledgedSectionRef.current !== section) {
      acknowledgedSectionRef.current = section;
      console.log('[NavBadges] acknowledge section', section);
      acknowledge(section);
    }
  }, [location.pathname, acknowledge, badges]);

  return (
    <NavBadgesContext.Provider value={{ badges, acknowledge }}>
      {children}
    </NavBadgesContext.Provider>
  );
}

export function useNavBadges(): NavBadgesContextType {
  const ctx = useContext(NavBadgesContext);
  if (!ctx) {
    throw new Error('useNavBadges must be used within NavBadgesProvider');
  }
  return ctx;
}

/**
 * Safe hook that returns null if outside provider (e.g. unauthenticated layout).
 */
export function useNavBadgesOptional(): NavBadgesContextType | null {
  return useContext(NavBadgesContext);
}
